import React from "react";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import MenuIcon from "@material-ui/icons/Menu";
import SearchIcon from "@material-ui/icons/Search";
import { Box, Flex, Link, Button } from "@chakra-ui/core";
import NextLink from "next/link";
import Router from "next/router";
import { theme } from "services/shared/StyledComponents";
import useInternationalization from "hooks/useInternationalization";
import useFirebaseSession from "hooks/useFirebaseSession";
import styled from "styled-components";
import { useQuery } from "@apollo/react-hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import gql from "graphql-tag";
import ErrorMessage from "components/shared/alerts/ErrorMessage";
import SuccessMessage from "components/shared/alerts/SuccessMessage";
import { useErrorMessageState } from "hooks/ui/useErrorMessageState";
import { useSuccessMessageState } from "hooks/ui/useSuccessMessageState";
import useViewport from "hooks/useViewport";
import { useRouter } from "next/router";
import {
  CtaType,
  useRecordCtaClicked,
} from "hooks/analytics/useRecordCtaClicked";
import { baseUrl } from "services/shared/Environment";
import { faGift } from "@fortawesome/free-solid-svg-icons";
import { useNavbarState } from "components/optimized/Navbar/Navbar.hooks";

export const NavbarContainer = styled<any>(AppBar)<any>`
  box-shadow: none;
  z-index: 1030;
  background-color: white;
`;

export const Logo = styled.img.attrs((props: any) => ({
  src: props.visitorIsFromIndia
    ? "/static/lettermark_logo.png"
    : "/static/wordmark_logo.png",
  height: "25",
  width: props.visitorIsFromIndia ? "25" : "100",
  alt: "logo",
}))<any>``;

export function NavbarContent({ children }) {
  return (
    <Toolbar>
      <Flex alignItems="center" flexGrow={1} position="relative">
        {children}
      </Flex>
    </Toolbar>
  );
}

export function CenterContentContainer({ children }) {
  return (
    <Flex
      pointerEvents="none"
      position="absolute"
      width="100%"
      flexGrow={1}
      justifyContent="center"
      alignItems="center"
    >
      {children}
    </Flex>
  );
}

export function LeftContentContainer({ children }) {
  return (
    <Flex flexGrow={1} alignItems="center" justifyContent="flex-start">
      {children}
    </Flex>
  );
}

export function RightContentContainer({ children }) {
  return (
    <Flex flexGrow={1} alignItems="center" justifyContent="flex-end">
      {children}
    </Flex>
  );
}

export function MenuButton({ onClick }) {
  return (
    <IconButton aria-label="Menu" onClick={onClick}>
      <MenuIcon />
    </IconButton>
  );
}

export function SearchButton({ onClick }) {
  return (
    <IconButton aria-label="Search" onClick={onClick}>
      <SearchIcon />
    </IconButton>
  );
}

export const TextLinkTag = styled<any>(Link)`
  font-family: ${(props) => props.theme.fonts.poppins};
  color: black;
`;

export const ActionButton = styled<any>(Button).attrs({
  bg: theme.colors.orange,
  _active: { backgroundColor: theme.colors.orange },
  _hover: { backgroundColor: theme.colors.orange },
})`
  font-family: ${(props) => props.theme.fonts.poppins};
  color: white;
`;

export function NavbarLinkContainer({ children }) {
  return <Box textTransform="uppercase">{children}</Box>;
}

export function NavbarTextLink({ children, href }) {
  return (
    <NavbarLinkContainer>
      <TextLink href={href}>{children}</TextLink>
    </NavbarLinkContainer>
  );
}

export function NavbarButtonLink({ children, href, label = null }) {
  return (
    <NavbarLinkContainer>
      <ButtonLink href={href}>{children}</ButtonLink>
    </NavbarLinkContainer>
  );
}

export function TextLink({ children, href }) {
  return (
    <NextLink href={href}>
      <TextLinkTag fontWeight="medium">{children}</TextLinkTag>
    </NextLink>
  );
}

export function ButtonLink({ children, href, label = null }) {
  return (
    <ActionButton
      size="sm"
      textTransform="uppercase"
      onClick={() => Router.push(href)}
      fontWeight="medium"
      aria-label={label}
    >
      {children}
    </ActionButton>
  );
}

export function AboutUsLink() {
  return (
    <Box ml={2} display={["none", "block"]}>
      <NavbarTextLink href="/about">About Us</NavbarTextLink>
    </Box>
  );
}

export function LoginLink() {
  return (
    <Box mr={4} display={["none", "block", "block"]}>
      <NavbarTextLink href="/login">Login</NavbarTextLink>
    </Box>
  );
}

export function SubscribeLink() {
  const { pathname } = useRouter();
  const url = "thejuggernaut.com" + pathname;
  return (
    <div>
      <Box mr={2} display={["none", "block"]}>
        <NavbarButtonLink href="/subscribe">Try for free</NavbarButtonLink>
      </Box>
    </div>
  );
}

export function WhySubscribeLink() {
  const recordCtaClicked = useRecordCtaClicked();

  return (
    <Box
      onClick={() => {
        recordCtaClicked({
          ctaType: CtaType.button,
          ctaText: "Why Subscribe",
          ctaDestinationUrl: `${baseUrl()}/why-subscribe`,
        });
      }}
      mr={2}
      display={["none", "block"]}
    >
      <NavbarButtonLink href="/why-subscribe">Why Subscribe?</NavbarButtonLink>
    </Box>
  );
}

export function ReferralLink() {
  const recordCtaClicked = useRecordCtaClicked();
  const { isMobileOrSmaller } = useViewport();
  const desktopCTAText = "Give $20, Get $20";

  const ctaText = isMobileOrSmaller ? (
    <FontAwesomeIcon icon={faGift} size="lg" />
  ) : (
    desktopCTAText
  );
  const linkHrefPath = "/refer";

  const onClickReferralLink = () => {
    recordCtaClicked({
      ctaType: CtaType.button,
      ctaText: isMobileOrSmaller ? "gift_icon" : desktopCTAText,
      ctaDestinationUrl: `${baseUrl()}${linkHrefPath}`,
    });
  };

  return (
    <Box onClick={onClickReferralLink} mr={2} display={"block"}>
      <NavbarButtonLink label={"Gift"} href={linkHrefPath}>
        {ctaText}
      </NavbarButtonLink>
    </Box>
  );
}

export function LoggedOutLinks({ showSubscribe }) {
  return (
    <>
      <LoginLink />
      {showSubscribe && <SubscribeLink />}
    </>
  );
}

export function LoggedInLinks({ userIsSubscribed }) {
  return <>{userIsSubscribed && <ReferralLink />}</>;
}

export function HomepageLink({ visitorIsFromIndia }) {
  return (
    <Box pointerEvents="all">
      <NextLink href="/">
        <a>
          <Logo visitorIsFromIndia={visitorIsFromIndia} />
        </a>
      </NextLink>
    </Box>
  );
}

export function Navbar({
  showSubscribe,
  showLeftNavContent,
  visitorIsFromIndia,
  userIsLoggedIn,
  userIsSubscribed,
  onClickMenuButton,
  children,
}) {
  
  const { isMobileOrSmaller } = useViewport();

  return (
    <>
      <NavbarContainer position={isMobileOrSmaller ? "static" : "fixed"}>
        {children}
        <NavbarContent>
          {showLeftNavContent && (
            <LeftContentContainer>
              <MenuButton onClick={onClickMenuButton} />
              <SearchButton onClick={() => Router.push("/search")} />
              <AboutUsLink />
            </LeftContentContainer>
          )}
          <CenterContentContainer>
            <HomepageLink visitorIsFromIndia={visitorIsFromIndia} />
          </CenterContentContainer>
          <RightContentContainer>
            {userIsLoggedIn ? (
              <LoggedInLinks userIsSubscribed={userIsSubscribed} />
            ) : (
              <LoggedOutLinks showSubscribe={showSubscribe} />
            )}
          </RightContentContainer>
        </NavbarContent>
      </NavbarContainer>
    </>
  );
}

const subscriptionCheckQuery = gql`
  query subscriptionCheckQuery($firebaseId: ID!) {
    user(firebaseId: $firebaseId) {
      id
      subscriptionHasExpired
    }
  }
`;

export function useSubscriptionCheck() {
  const { loggedInUserId } = useFirebaseSession();

  const { loading, data } = useQuery(subscriptionCheckQuery, {
    variables: { firebaseId: loggedInUserId },
  });
  const subscriptionHasExpired = data?.user?.subscriptionHasExpired ?? true;
  return { subscriptionHasExpired, loading };
}

export interface NavbarProps {
  menuOpen: boolean;
  toggleMenu: () => void
}

function useShowSubscribe() {
  const { pathname } = useRouter();
  const showSubscribe = pathname !== "/subscribe";
  return { showSubscribe };
}

function useShowLeftNavContent() {
  const { pathname } = useRouter();
  const showLeftNavContent = pathname !== "/subscribe" && pathname !== "/gift";
  return { showLeftNavContent };
}

export function NavbarWithState(props: NavbarProps) {

  const {
    showSubscribe,
    showLeftNavContent,
    visitorIsFromIndia,
    userIsLoggedIn,
    toggleMenu,
    subscriptionHasExpired,
    errorMessageState,
    successMessageState
  } = useNavbarState(props)

  const {errorMessageId, errorMessageShown, errorMessage, hideErrorMessage } = errorMessageState;
  const { successMessageId, successMessageShown, successMessage, hideSuccessMessage } = successMessageState;

  return (
    <Navbar
      showSubscribe={showSubscribe}
      showLeftNavContent={showLeftNavContent}
      visitorIsFromIndia={visitorIsFromIndia}
      userIsLoggedIn={userIsLoggedIn}
      userIsSubscribed={!subscriptionHasExpired}
      onClickMenuButton={toggleMenu}
    >
      <ErrorMessage
        key={errorMessageId}
        show={errorMessageShown}
        text={errorMessage}
        onClickClose={hideErrorMessage}
      />
      <SuccessMessage
        key={successMessageId}
        show={successMessageShown}
        text={successMessage}
        onClickClose={hideSuccessMessage}
      />
    </Navbar>
  );
}

NavbarWithState.defaultProps = {
  menuOpen: true,
  toggleMenu: (open: boolean) => () => {},
};
